import React from 'react';
import { Tooltip as MuiTooltip, TooltipProps as MuiTooltipProps } from '@mui/material';
import { SxProps, Theme } from '@mui/material/styles';
import { Box } from '../Box';

// Pounce alignment type
export type Alignment =
  | 'bottom-left'
  | 'bottom-center'
  | 'bottom-right'
  | 'top-left'
  | 'top-center'
  | 'top-right'
  | 'left-bottom'
  | 'left-center'
  | 'left-top'
  | 'right-bottom'
  | 'right-center'
  | 'right-top';

/**
 * Default wrapper component that matches V1 Pounce styling exactly
 */
const DefaultWrapper: React.FC<React.PropsWithChildren> = ({ children }) => <Box>{children}</Box>;

/**
 * Maps Pounce alignment to MUI placement
 */
function mapAlignmentToPlacement(alignment: Alignment): MuiTooltipProps['placement'] {
  const alignmentMap: Record<Alignment, MuiTooltipProps['placement']> = {
    'bottom-left': 'bottom-start',
    'bottom-center': 'bottom',
    'bottom-right': 'bottom-end',
    'top-left': 'top-start',
    'top-center': 'top',
    'top-right': 'top-end',
    'left-bottom': 'left-start',
    'left-center': 'left',
    'left-top': 'left-end',
    'right-bottom': 'right-start',
    'right-center': 'right',
    'right-top': 'right-end',
  };
  return alignmentMap[alignment];
}

export interface TooltipProps extends Omit<MuiTooltipProps, 'content' | 'title'> {
  /** The string or HTML that the tooltip will show */
  content: string | React.ReactElement;

  /** The wrapper that contains the content */
  wrapper?: React.ElementType;

  /** Optional set of props passed to the wrapper */
  wrapperProps?: any;

  /** The alignment of the tooltip relative to the content */
  alignment?: Alignment;

  /** Additional styles to apply to the tooltip */
  sx?: SxProps<Theme>;

  /** The element that triggers the tooltip */
  children: React.ReactElement;
}

/**
 * Tooltip - Extends MUI Tooltip
 *
 * A tooltip is a helper that shows some helping text when hovering or clicking something
 */
export const Tooltip = React.forwardRef<HTMLDivElement, TooltipProps>(function Tooltip(
  {
    content,
    alignment = 'right-center',
    wrapper = DefaultWrapper,
    disableInteractive = true,
    enterDelay = 200,
    enterNextDelay = 200,
    leaveDelay = 400,
    children,
    wrapperProps,
    arrow = true,
    sx,
    ...rest
  },
  ref,
) {
  const Wrapper = wrapper;
  const placement = mapAlignmentToPlacement(alignment);

  // Wrap content with the wrapper component
  const wrappedContent = <Wrapper {...wrapperProps}>{content}</Wrapper>;

  return (
    <MuiTooltip
      ref={ref}
      title={content ? wrappedContent : null}
      placement={placement}
      disableInteractive={disableInteractive}
      enterDelay={enterDelay}
      enterNextDelay={enterNextDelay}
      leaveDelay={leaveDelay}
      slotProps={{
        tooltip: { sx },
        transition: {
          timeout: 150,
        },
      }}
      arrow={arrow}
      {...rest}
    >
      {children}
    </MuiTooltip>
  );
});
